import { tracked } from '@glimmer/tracking';
import { ChannelCommissionChannel } from 'uplisting-frontend/models/channel-commission';
import BaseRepositoryService, {
  type GenericChangeset,
} from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { ChannelCommissionValidation } from 'uplisting-frontend/validations';

const amountPerChannel = {
  [ChannelCommissionChannel.bookingDotCom]: 15,
  [ChannelCommissionChannel.airbnbOfficial]: 15,
  [ChannelCommissionChannel.homeAway]: 8,
  [ChannelCommissionChannel.google]: 6,
  [ChannelCommissionChannel.direct]: 0,
};

export default class ChannelCommissionRepositoryService extends BaseRepositoryService<
  ModelRegistry['channel-commission']
> {
  recordName = 'channel-commission';
  implementMethods = [
    'findAll',
    'unloadAll',
    'createRecord',
    'buildChangeset',
    'peekAll',
  ];

  validation = ChannelCommissionValidation;

  @tracked
  bookingDotComChangeset!: GenericChangeset<
    ModelRegistry['channel-commission']
  >;

  @tracked
  airbnbOfficialChangeset!: GenericChangeset<
    ModelRegistry['channel-commission']
  >;

  @tracked homeAwayChangeset!: GenericChangeset<
    ModelRegistry['channel-commission']
  >;
  @tracked googleChangeset!: GenericChangeset<
    ModelRegistry['channel-commission']
  >;
  @tracked directChangeset!: GenericChangeset<
    ModelRegistry['channel-commission']
  >;

  public setDefaultRecords(): void {
    const commissions = this.peekAll();

    this.bookingDotComChangeset = this.changesetFor(
      commissions,
      ChannelCommissionChannel.bookingDotCom,
    );

    this.airbnbOfficialChangeset = this.changesetFor(
      commissions,
      ChannelCommissionChannel.airbnbOfficial,
    );

    this.homeAwayChangeset = this.changesetFor(
      commissions,
      ChannelCommissionChannel.homeAway,
    );

    this.googleChangeset = this.changesetFor(
      commissions,
      ChannelCommissionChannel.google,
    );

    this.directChangeset = this.changesetFor(
      commissions,
      ChannelCommissionChannel.direct,
    );
  }

  public changesetFor(
    commissions: ModelRegistry['channel-commission'][],
    channel: ChannelCommissionChannel,
  ): GenericChangeset<ModelRegistry['channel-commission']> {
    const commission = commissions.find((item) => item.channel === channel);

    return this.buildChangeset(
      commission ||
        this.createRecord({
          channel,
          amount: amountPerChannel[channel],
        }),
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/channel-commission': ChannelCommissionRepositoryService;
  }
}
